import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Ticket } from "lucide-react";
import { toast } from "sonner";

import { guestRewardsApi } from "@/api/guest/guestRewardsApi.js";
import GuestLayout from "@/components/guest/GuestLayout.jsx";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSession } from "@/hooks/useSession.js";
import {
  formatCouponStatus,
  formatGuestDate,
  getApiData,
  getApiMessage,
  read,
} from "@/utils/guestRewardsFormat.js";
import {
  FullScreenLoading,
  GuestPageShell,
  LoadingBlock,
  RegistrationPrompt,
} from "@/pages/guest/GuestProfilePage.jsx";

const GuestCouponRedeemPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session, hotel, room, loading: sessionLoading } = useSession();
  const [couponData, setCouponData] = useState(null);
  const [loading, setLoading] = useState(true);

  const code = useMemo(() => (searchParams.get("code") || "").trim(), [searchParams]);

  useEffect(() => {
    if (!sessionLoading && !session) navigate("/guest/expired", { replace: true });
  }, [sessionLoading, session, navigate]);

  useEffect(() => {
    const loadRedeemContext = async () => {
      if (!session) return;
      try {
        setLoading(true);
        const couponsResponse = await guestRewardsApi.getCoupons();
        setCouponData(getApiData(couponsResponse));
      } catch (error) {
        toast.error(getApiMessage(error, "Unable to load coupon details."));
      } finally {
        setLoading(false);
      }
    };
    loadRedeemContext();
  }, [session]);

  if (sessionLoading) return <FullScreenLoading />;
  if (!session) return null;

  const requiresRegistration = read(couponData, "requiresRegistration", "RequiresRegistration", false);
  const coupons = read(couponData, "eventCoupons", "EventCoupons", []);
  const coupon = coupons.find((item) =>
    String(read(item, "couponCode", "CouponCode", "")).toLowerCase() === code.toLowerCase()
  );
  const packageEvent = read(coupon, "packageEvent", "PackageEvent", {});
  const couponStatus = read(coupon, "status", "Status", 0);
  const couponActive = isActiveCouponStatus(couponStatus);
  const remaining = Number(read(coupon, "usageRemaining", "UsageRemaining", 0));
  const disabledReason = getDisabledReason({ coupon, couponActive, remaining });

  return (
    <>
      <Helmet>
        <title>Redeem coupon - Guest Portal</title>
      </Helmet>
      <GuestLayout hotel={hotel} room={room} session={session}>
        <GuestPageShell onBack={() => navigate("/guest/coupons")}>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={() => navigate("/guest/coupons")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Coupons
            </Button>
          </div>

          <div className="rounded-3xl border border-white/75 bg-white/90 p-5 shadow-[0_22px_52px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:p-6">
            <div className="flex items-center gap-3">
              <Ticket className="h-7 w-7 text-[#923536]" />
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Redeem coupon</h1>
                <p className="mt-1 text-sm text-slate-600">Review the scanned package event coupon before showing it to hotel staff.</p>
              </div>
            </div>

            {loading ? (
              <LoadingBlock text="Loading coupon..." />
            ) : requiresRegistration ? (
              <RegistrationPrompt
                onRegister={() =>
                  navigate(`/guest/register?returnTo=${encodeURIComponent(`/guest/coupons/redeem?code=${encodeURIComponent(code)}`)}`)
                }
              />
            ) : !code ? (
              <Notice title="No coupon code found" text="This QR code does not include a coupon code." />
            ) : !coupon ? (
              <Notice title="Coupon unavailable" text="This coupon is not available for your current guest profile." />
            ) : (
              <div className="mt-5 space-y-4">
                <article className="rounded-2xl border bg-white p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Coupon code</p>
                      <p className="mt-1 text-2xl font-bold tracking-[0.08em] text-[#923536]">
                        {read(coupon, "couponCode", "CouponCode", code)}
                      </p>
                      <p className="mt-2 text-sm text-slate-600">
                        {read(packageEvent, "description", "Description", read(packageEvent, "title", "Title", "Package event"))}
                      </p>
                    </div>
                    <Badge variant="outline">{formatCouponStatus(read(coupon, "status", "Status"))}</Badge>
                  </div>
                  <div className="mt-4 grid gap-3 text-sm text-slate-600 sm:grid-cols-3">
                    <Info label="Event" value={read(packageEvent, "title", "Title", "Package event")} />
                    <Info label="Location" value={read(packageEvent, "locationName", "LocationName", "Hotel")} />
                    <Info label="Remaining" value={remaining} />
                    <Info label="Valid to" value={formatGuestDate(read(coupon, "validTo", "ValidTo"))} />
                  </div>
                </article>

                <div className="rounded-2xl border bg-slate-50 p-4">
                  <p className="font-semibold text-slate-900">How to use this coupon</p>
                  <p className="mt-2 text-sm text-slate-600">
                    Show this QR code or coupon code to reception or the service counter. Staff will verify and redeem the exact package event.
                  </p>
                </div>

                {(!couponActive || remaining <= 0) && (
                  <Notice
                    title="Cannot redeem yet"
                    text={disabledReason}
                  />
                )}

                <Button
                  type="button"
                  onClick={() => navigate("/guest/coupons")}
                  className="w-full bg-[#923536] text-white hover:bg-[#7f2d2e] sm:w-auto"
                >
                  Back to coupons
                </Button>
              </div>
            )}
          </div>
        </GuestPageShell>
      </GuestLayout>
    </>
  );
};

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-[0.12em] text-slate-500">{label}</p>
    <p className="mt-1 font-semibold text-slate-900">{value}</p>
  </div>
);

const Notice = ({ title, text }) => (
  <div className="mt-5 rounded-2xl border border-dashed bg-white p-5 text-sm text-slate-600">
    <p className="font-semibold text-slate-900">{title}</p>
    <p className="mt-1">{text}</p>
  </div>
);

const isActiveCouponStatus = (value) => {
  if (Number(value) === 1) return true;
  return String(value || "").trim().toLowerCase() === "active";
};

const getDisabledReason = ({ coupon, couponActive, remaining }) => {
  if (!coupon) return "This coupon is not available for your current guest profile.";
  if (!couponActive || remaining <= 0) return "This coupon is no longer active for redemption.";
  return "This coupon cannot be redeemed right now.";
};

export default GuestCouponRedeemPage;
